/**
 * Platform-agnostic database interface
 * Provides a common interface for database operations across platforms
 */

import { logger } from '../../utils/logger';

export interface DatabaseRow {
  [key: string]: any;
}

export interface DatabaseResult {
  rows: DatabaseRow[];
  rowCount: number;
}

export interface DatabaseInterface {
  initialize(): Promise<void>;
  close(): Promise<void>;
  execAsync(sql: string): Promise<void>;
  getFirstAsync(sql: string, params?: any[]): Promise<DatabaseRow | null>;
  getAllAsync(sql: string, params?: any[]): Promise<DatabaseRow[]>;
  getAsync(sql: string, params?: any[]): Promise<DatabaseResult>;
  runAsync(
    sql: string,
    params?: any[]
  ): Promise<{ lastInsertRowId: number; changes: number }>;
  isInitialized(): boolean;
}

/**
 * Web-specific database implementation using IndexedDB
 */
export class WebDatabase implements DatabaseInterface {
  private db: IDBDatabase | null = null;
  private isInitialized = false;

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('coffeeplexity_journeys', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create tables as object stores
        if (!db.objectStoreNames.contains('journeys')) {
          db.createObjectStore('journeys', {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
        if (!db.objectStoreNames.contains('weeks')) {
          db.createObjectStore('weeks', { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains('activities')) {
          db.createObjectStore('activities', {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
        if (!db.objectStoreNames.contains('progress')) {
          db.createObjectStore('progress', {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
        if (!db.objectStoreNames.contains('migrations')) {
          db.createObjectStore('migrations', { keyPath: 'version' });
        }
      };
    });
  }

  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.isInitialized = false;
    }
  }

  async execAsync(sql: string): Promise<void> {
    // For web, we'll implement basic SQL parsing and execution
    // This is a simplified implementation that just logs operations
    if (sql.includes('CREATE TABLE')) {
      logger.debug('Web database: Creating table structure (simplified)');
      // In a real implementation, you would parse the SQL and create IndexedDB object stores
    } else if (sql.includes('PRAGMA')) {
      logger.debug('Web database: Skipping SQLite-specific pragma:', sql);
    } else {
      logger.debug('Web database exec:', sql);
    }
  }

  async getFirstAsync(
    sql: string,
    params: any[] = []
  ): Promise<DatabaseRow | null> {
    // Simplified implementation for web
    logger.debug('Web database getFirst:', sql, params);
    return null;
  }

  async getAllAsync(sql: string, params: any[] = []): Promise<DatabaseRow[]> {
    // Simplified implementation for web
    logger.debug('Web database getAll:', sql, params);
    return [];
  }

  async getAsync(sql: string, params: any[] = []): Promise<DatabaseResult> {
    // Simplified implementation for web
    logger.debug('Web database get:', sql, params);
    return { rows: [], rowCount: 0 };
  }

  async runAsync(
    sql: string,
    params: any[] = []
  ): Promise<{ lastInsertRowId: number; changes: number }> {
    // Simplified implementation for web
    logger.debug('Web database run:', sql, params);
    return { lastInsertRowId: 0, changes: 0 };
  }

  isInitialized(): boolean {
    return this.isInitialized;
  }
}

/**
 * Native database implementation using expo-sqlite
 */
export class NativeDatabase implements DatabaseInterface {
  private db: any = null;
  private isInitialized = false;

  async initialize(): Promise<void> {
    // Dynamic import to avoid web bundling issues
    const SQLite = await import('expo-sqlite');
    this.db = await SQLite.openDatabaseAsync('coffeeplexity_journeys.db');
    this.isInitialized = true;
  }

  async close(): Promise<void> {
    if (this.db) {
      await this.db.closeAsync();
      this.db = null;
      this.isInitialized = false;
    }
  }

  async execAsync(sql: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.execAsync(sql);
  }

  async getFirstAsync(
    sql: string,
    params: any[] = []
  ): Promise<DatabaseRow | null> {
    if (!this.db) throw new Error('Database not initialized');
    const result = await this.db.getFirstAsync(sql, params);
    return result;
  }

  async getAllAsync(sql: string, params: any[] = []): Promise<DatabaseRow[]> {
    if (!this.db) throw new Error('Database not initialized');
    const result = await this.db.getAllAsync(sql, params);
    return result;
  }

  async getAsync(sql: string, params: any[] = []): Promise<DatabaseResult> {
    if (!this.db) throw new Error('Database not initialized');
    const result = await this.db.getAsync(sql, params);
    return result;
  }

  async runAsync(
    sql: string,
    params: any[] = []
  ): Promise<{ lastInsertRowId: number; changes: number }> {
    if (!this.db) throw new Error('Database not initialized');
    const result = await this.db.runAsync(sql, params);
    return result;
  }

  isInitialized(): boolean {
    return this.isInitialized;
  }
}

/**
 * Check if running on web platform
 */
export function isWebPlatform(): boolean {
  return typeof window !== 'undefined' && typeof indexedDB !== 'undefined';
}

/**
 * Factory function to create the appropriate database implementation
 */
export function createDatabase(): DatabaseInterface {
  if (isWebPlatform()) {
    // Web environment
    return new WebDatabase();
  } else {
    // Native environment
    return new NativeDatabase();
  }
}
